import { LinkButton } from '@/components/elements/forms/buttons';
import ContainerLayout from '@/components/layouts/ContainerLayout';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import { APP_ROUTES } from '@/constants/pages';
import { isServer } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import DealsSectionPreview from '../../modules/DealSectionPreview';
import { DealDetailSearchPreview, DealErrorType } from '../../types/dealDetails';

function getErrorLocale(errorType: DealErrorType): { title: string; description: string; cta: string } {
  if (errorType === 'error') {
    return {
      title: _t('features.deals.pages.DealNotAvailable.title.error'),
      description: _t('features.deals.pages.DealNotAvailable.description.error'),
      cta: _t('features.deals.pages.DealNotAvailable.cta'),
    };
  }

  const isCustomerError = errorType === 'targetGroup' || errorType === 'maxCustomerCount';

  if (isCustomerError) {
    return {
      title: _t('features.deals.pages.DealNotAvailable.title.customerError'),
      description: _t('features.deals.pages.DealNotAvailable.description.customerError'),
      cta: _t('features.deals.pages.DealNotAvailable.cta'),
    };
  }

  return {
    title: _t('features.deals.pages.DealNotAvailable.title.notAvailableError'),
    description: _t('features.deals.pages.DealNotAvailable.description.notAvailableError'),
    cta: _t('features.deals.pages.DealNotAvailable.cta'),
  };
}

type DealNotAvailableProps = {
  sections: DealDetailSearchPreview;
  errorType: DealErrorType;
  staticContext?: { status?: number };
};

const DealNotAvailable = ({ sections, errorType, staticContext }: DealNotAvailableProps) => {
  const dispatch = useAppDispatch();
  const { isMobileView } = useMobileView();

  if (isServer && staticContext) {
    staticContext.status = errorType === 'not_found' ? 404 : 410;
  }

  const { title, description, cta } = getErrorLocale(errorType);

  return (
    <PageViewLayout type="mainView" back wrapperClass="bg-surfaceColors-surface_mp" hideBottomNavBar>
      <ContainerLayout>
        <div className="px-md md:px-xl py-xl space-y-xl md:space-y-cv-margin mx-auto max-w-screen-lg">
          {/* Breadcrubs */}
          <div className="">{/* <Breadcrumbs items={[{ title: 'Vad', url: '/' }]} /> */}</div>
          {/* Content */}
          <div className="gap-lg flex flex-col-reverse md:flex-row md:items-start md:justify-between">
            <div className="space-y-xl md:space-y-2xl flex max-w-lg flex-col">
              <h1 className="text-fg_primary text-h-xl font-semibold">{title}</h1>
              <p className="text-fg_secondary text-l">{description}</p>
              <LinkButton variant="primary" to={APP_ROUTES.DEALS} className="w-max">
                {cta}
              </LinkButton>
            </div>
            {isMobileView ? (
              <img src="/images/deal-gone-mobile.png" alt="" aria-hidden width={200} height={132} />
            ) : (
              <img src="/images/deal-gone-desktop.png" alt="" aria-hidden width={400} height={296} />
            )}
          </div>
        </div>
      </ContainerLayout>

      <ContainerLayout onlyAbove2xl>
        {sections.map((section) => (
          <div key={section.id}>
            <DealsSectionPreview section={section} dispatch={dispatch} />
          </div>
        ))}
      </ContainerLayout>
    </PageViewLayout>
  );
};

export default DealNotAvailable;
